import React, { useCallback, useEffect, useState } from "react";
import { useApi } from "./UserProvider";

export const NotesContext = React.createContext(null);

export const NotesProvider = ({children}) => {


    const { api } = useApi();

    const [notes, setNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(true);
    const [notebooks, setNotebooks] = useState([]);
    const [notebooksLoading, setNotebooksLoading] = useState(true);

    const sortNotesByNewest = (notesArray) => {
        return [...notesArray].sort((a, b) => new Date(b.noteTimestamp) - new Date(a.noteTimestamp));
    }

    const notebooksHandler = async ({notebookUuid, action, changes}) => {
        const previousNotebooks = structuredClone(notebooks);

        if(action === "UPDATE NOTEBOOK")
        {
            const notebookCopyBase = structuredClone(notebooks.find(nbs => nbs.notebookUuid === notebookUuid));
            if(!notebookCopyBase) return;

            const updatedNotebook = {
                ...notebookCopyBase,
                notebookName: changes.notebookName ?? notebookCopyBase.notebookName,
                notebookStatus: changes.notebookStatus ?? notebookCopyBase.notebookStatus
            };

            const isArchived = changes.notebookStatus === "Archived";

            const nextState = isArchived
                    ? notebooks.filter(nbs => nbs.notebookUuid !== notebookUuid)
                    : notebooks.map(nbs =>
                        nbs.notebookUuid !== notebookUuid
                            ? nbs
                            : updatedNotebook
                    );

            setNotebooks(nextState);

            try
            {
                await api.request("notebooks/update", updatedNotebook);

                if(isArchived)
                {
                    return ({
                        status: "NOTEBOOK_ARCHIVED",
                        reason: "NOTEBOOK_ARCHIVED"
                    });
                }

                return ({
                    status: "NOTEBOOK_UPDATED"
                });
            }
            catch (err)
            {
                console.error(err);
                setNotebooks(previousNotebooks);

                return ({
                    status: "NOTEBOOK_NOT_UPDATED",
                    reason: "SERVER_ERROR"
                });
            }
        }
        else if(action === "CREATE NOTEBOOK")
        {
            const newNotebook = {
                ...changes
            };

            setNotebooks(prev => [...prev, newNotebook]);

            try
            {
                 await api.request("notebooks/create", newNotebook);

                return ({
                    status: "NOTEBOOK_CREATED"
                });
            }
            catch (err)
            {
                console.error(err);
                setNotebooks(previousNotebooks);

                return ({
                    status: "NOTEBOOK_NOT_CREATED",
                    reason: "SERVER_ERROR"
                });
            }
        }
    }

    const notesHandler = async ({ noteUuid, action, changes }) => {
        const previousNotes = structuredClone(notes);

        if(action === "CREATE NOTE")
        {
            const newNote = structuredClone(changes);
            setNotes(prev => sortNotesByNewest([...prev, newNote]));
            try
            {
                await api.request("notes/create", newNote);

                return ({
                    status: "NOTE_CREATED"
                });
            }
            catch (err)
            {
                console.error(err);

                setNotes(previousNotes);

                return ({
                    status: "NOTE_NOT_CREATED",
                    reason: "SERVER_ERROR"
                });
            }
        }

        if(action === "UPDATE NOTE")
        {
            const noteCopy = structuredClone(notes.find(n => n.noteUuid === noteUuid));

            if(!noteCopy) return;

            noteCopy.noteTitle = changes.noteTitle ?? noteCopy.noteTitle;
            noteCopy.noteStatus = changes.noteStatus ?? noteCopy.noteStatus;
            noteCopy.noteTimestamp = (new Date()).toISOString();

            const isArchived = changes.noteStatus === "Archived";

            const nextState = isArchived
                ? notes.filter(n => n.noteUuid !== noteUuid)
                : notes.map(n => n.noteUuid !== noteUuid ? n : noteCopy);

            setNotes(sortNotesByNewest(nextState));

            const apiNote = {
                ...noteCopy
            };

            try
            {
                const res = await api.request("notes/update", apiNote);

                if(isArchived)
                {
                    return ({
                        status: "NOTE_ARCHIVED",
                        reason: "NOTE_ARCHIVED"
                    });
                }
                else if(res.status === "NOTE_UPDATED")
                {
                    return ({
                        status: "NOTE_UPDATED"
                    });
                }

                return ({
                    status: "NOTE_UNKNOWN_RESPONSE",
                    data: res
                });
            }
            catch(err)
            {
                console.error(err);

                if(err?.status === 409 && err?.body?.reason === "NOTES_RENAME_VERSION_MISMATCH")
                {
                    const serverNote = err.body;

                    setNotes(prev =>
                        prev.map(n => {
                            if (n.noteUuid !== noteUuid) return n;

                            return {
                                ...n,
                                noteTitle: serverNote.noteTitle,
                                noteTitleVersion: serverNote.noteTitleVersion,
                                noteTimestamp: serverNote.noteTimestamp
                            };
                        })
                    );

                    return {
                        status: "NOTE_UPDATED",
                        reason: "NOTES_RENAME_VERSION_MISMATCH"
                    };
                }

                setNotes(previousNotes);

                return {
                    status: "NOTE_NOT_UPDATED",
                    reason: "SERVER_ERROR"
                };
            }
        }
    };

    const fetchNotes = useCallback(async () => {
        try
        {
            const nts = await api.request("notes/query/all");
            setNotes(nts);
        }
        catch(err)
        {
            console.error(err);
        }
        finally
        {
            setNotesLoading(false);
        }
    }, [api])

    const fetchNotebooks = useCallback(async () => {
        try
        {
            const nbs = await api.request("notebooks/query/all");
            setNotebooks(nbs);
        }
        catch(err)
        {
            console.error(err);
        }
        finally
        {
            setNotebooksLoading(false);
        }
    }, [api])

    useEffect(() => {
        fetchNotebooks();
        fetchNotes();
    }, [fetchNotebooks, fetchNotes])

    useEffect(() => {
        fetchNotes();

        const interval = setInterval(() => {
            fetchNotes();
        }, 60000);

        return () => clearInterval(interval);
    }, [api, fetchNotebooks, fetchNotes]);

    return (
        <NotesContext.Provider value={{
            notes,
            notesLoading,
            notebooks,
            notebooksLoading,
            notebooksHandler,
            notesHandler
        }}>
            {children}
        </NotesContext.Provider>
    )
}